import { ColDef, GridApi, IRowNode } from "ag-grid-community";
import { action, makeObservable, observable } from "mobx";
import ProgressIndicatorModel, {
  IProgressIndicatorModel
} from "../../../../../../components/widgets/ProgressIndicator/ProgressIndicator_model";
import PermissionsContext from "../../../../../../contexts/permissions/PermissionsContext";
import { PermissionFields } from "../../../../../../contexts/permissions/PermissionsTypes";
import { Category, EntityTypes, GridTypes, TrainingComponentField } from "../../../../../../enums";
import { getEntityNameMicroFormFields } from "../../../../../../pages/change/forms/microForm/getEntityNameMicroFormFields";
import { SingleFormModel } from "../../../../../../pages/change/forms/singleFormModel/SingleForm_model";
import ProjectTeamUserPermissionsApi, {
  ProjectTeamUserPermissionsApi as IProjectTeamUserPermissionsApi
} from "../../../../../../services/api/v2/projectTeamUserPermissions/ProjectTeamUserPermissions.api";
import GridToastService from "../../../../../../services/local/gridToastService/GridToastService";
import { IGridToastService } from "../../../../../../services/local/gridToastService/IGridToastService";
import I18n from "../../../../../localization/I18n";
import { IModalContextModel } from "../../../../../modalZ/context/IModalContext";
import ModalContext from "../../../../../modalZ/context/ModalContext";
import ToasterService, { IToasterService } from "../../../../../toaster/ToasterService";
import { TOASTER_TOAST_TIME } from "../../../../../toaster/Toaster_model";
import { GridModalBuilder } from "../../modals/GridModal_builder";
import { IGridUiAction } from "../base/AppGridToolbarActions_view";
import { AppGridState } from "../base/AppGrid_view";
import { ColumnStateAddon } from "../base/addons/ColumnStateAddon";
import { FilterStoreAddon } from "../base/addons/FilterAddon/FilterStoreAddon";
import { TextWrapperAddon } from "../base/addons/TextWrapAddon";

import { AppGridToolbarType } from "../../types/AppGrid_types";
import {
  CommonColDefFieldNamesEnum,
  TrainingComponentColDefFieldNamesEnum
} from "../../../enums/AgGridColDefFieldNameEnum";
import TrainingTypesApi, {
  TrainingTypesApi as ITrainingTypesApi
} from "../../../../../../services/api/v2/trainingTypes/TrainingTypes.api";

import TrainingComponentsApi, {
  TrainingComponentsApi as ITrainingComponentsApi
} from "../../../../../../services/api/v2/TrainingComponents/TrainingComponents.api";
import {
  SHOW_CONFIRM_CREATION_MODAL,
  SHOW_TRAINING_COMPONENT_DELETE_CONFIRM_MODAL
} from "./TrainingComponentsGrid_modals";
import { GetTrainingComponentsGridActions } from "./TrainingComponentsGrid_actions";
import { TrainingComponentsGridColumnBuilder } from "./TrainingComponentsGrid_columns";

export class TrainingComponentsGridModel {
  organisationId: number;
  projectId: number;
  progressStatuses: FP.Generic.IKeyLabel[];
  trainingComponentsProvider: ITrainingComponentsApi;
  authUser: FP.Entities.IUser;
  filterStoreAddon: FilterStoreAddon;
  columnStateAddon: ColumnStateAddon;
  textWrapAddon: TextWrapperAddon;
  @observable isFilterChanged: boolean;
  @observable isColumnStateChanged: boolean;
  userCanViewTrainingComponents: boolean;
  userCanEditTrainingComponents: boolean;
  userCanViewActions: boolean;
  userCanViewProcesses: boolean;
  type: GridTypes = GridTypes.TRAINING_COMPONENTS_GRID;
  trainingComponentsGridColumnBuilder: TrainingComponentsGridColumnBuilder;
  modalService: IModalContextModel;
  httpProgress: IProgressIndicatorModel;
  @observable confirmationService: IModalContextModel;
  @observable selectedItems: number[] = [];
  toasterService: IToasterService;
  @observable isDuplicatingItems: boolean = false;
  @observable microTrainingComponentForm: SingleFormModel;
  gridApi: GridApi;
  @observable.ref gridActions: IGridUiAction[] = [];
  gridModalBuilder: GridModalBuilder;
  gridToastService: IGridToastService;
  projectTeamUserPermissionsProvider: IProjectTeamUserPermissionsApi = ProjectTeamUserPermissionsApi;
  trainingTypesProvider: ITrainingTypesApi = TrainingTypesApi;
  @observable projectTeamMembers: any[];
  @observable projectTrainingTypes: any[];
  @observable trainingTypes: any[];
  gridColumns: ColDef<any, any>[];
  urlSearchParams: URLSearchParams;
  gridToolbarType: AppGridToolbarType;
  gridType: EntityTypes | GridTypes;
  @observable isLoading: boolean = true;
  onFieldUpdate: () => Promise<void>;
  refreshDataHandler?: () => Promise<void>;
  @observable preSelectedItemIds: number[];

  constructor(
    organisationId: number,
    projectId: number,
    authUser: FP.Entities.IUser,
    urlSearchParams: URLSearchParams,
    gridToolbarType: AppGridToolbarType,
    preSelectedItemIds: number[],
    gridType: EntityTypes | GridTypes,
    onFieldUpdate: () => Promise<void>,
    refreshDataHandler?: () => Promise<void>
  ) {
    makeObservable(this);
    this.trainingComponentsProvider = TrainingComponentsApi;
    this.onFieldUpdate = onFieldUpdate;
    this.refreshDataHandler = refreshDataHandler;
    this.userCanViewTrainingComponents = PermissionsContext.canViewField(
      PermissionFields.PROJECTS,
      organisationId,
      projectId
    );
    this.userCanEditTrainingComponents = PermissionsContext.canEditField(
      PermissionFields.PROJECTS,
      organisationId,
      projectId
    );
    this.organisationId = organisationId;
    this.projectId = projectId;
    this.authUser = authUser;
    this.urlSearchParams = urlSearchParams;
    this.gridToolbarType = gridToolbarType;
    this.modalService = ModalContext;
    this.toasterService = ToasterService;
    this.confirmationService = ModalContext;
    this.httpProgress = ProgressIndicatorModel;
    this.setMicroAddForm();
    this.gridToastService = GridToastService;
    this.gridModalBuilder = new GridModalBuilder();
    this.selectedItems = preSelectedItemIds;
    this.preSelectedItemIds = preSelectedItemIds;
    this.gridType = gridType;
    this.generateAddons();
  }

  @action
  onMount = async () => {
    this.generateFn();
    this.setIsLoading(false);
  };

  @action
  generateFn = () => {
    this.generateGridConfig();
    this.generateActions();
  };

  @action
  setIsLoading = (isLoading: boolean) => {
    this.isLoading = isLoading;
  };

  onUnmount = () => {};

  generateAddons = () => {
    this.textWrapAddon = new TextWrapperAddon(this.projectId, EntityTypes.TRAINING_COMPONENTS);
    this.filterStoreAddon = new FilterStoreAddon({
      projectId: this.projectId,
      gridType: EntityTypes.TRAINING_COMPONENTS,
      filterHasChangedFn: this.setIsFilterChanged,
      urlSearchParams: this.urlSearchParams
    });

    this.columnStateAddon = new ColumnStateAddon({
      projectId: this.projectId,
      gridType: this.gridType ?? EntityTypes.TRAINING_COMPONENTS,
      columnOrderHasChangedFn: this.setIsColumnStateChanged
    });
  };

  generateGridConfig = () => {
    this.trainingComponentsGridColumnBuilder = new TrainingComponentsGridColumnBuilder({
      canEdit: this.userCanEditTrainingComponents && this.gridToolbarType !== "link-modal",
      organisationId: this.organisationId,
      projectId: this.projectId,
      userCanViewTrainingComponents: this.userCanViewTrainingComponents,
      projectTeamMembers: this.projectTeamMembers,
      refreshDataHandler: this.refreshDataHandler,
      onFieldUpdate: this.onFieldUpdate,
      trainingTypes: this.trainingTypes,
      columns: [
        CommonColDefFieldNamesEnum.Selected,
        CommonColDefFieldNamesEnum.Name,
        CommonColDefFieldNamesEnum.Description,
        TrainingComponentColDefFieldNamesEnum.ProgressStatus,
        TrainingComponentColDefFieldNamesEnum.TrainingType,
        CommonColDefFieldNamesEnum.Owner,
        CommonColDefFieldNamesEnum.NoteCount,
        CommonColDefFieldNamesEnum.Tags,
        TrainingComponentColDefFieldNamesEnum.TrainingModules,
        CommonColDefFieldNamesEnum.StartDate,
        CommonColDefFieldNamesEnum.EndDate,
        CommonColDefFieldNamesEnum.CreatedBy,
        CommonColDefFieldNamesEnum.CreatedAt,
        CommonColDefFieldNamesEnum.ModifiedBy,
        CommonColDefFieldNamesEnum.UpdatedAt
      ]
    });
    this.gridColumns = this.trainingComponentsGridColumnBuilder.generateColumnDefs();
  };

  generateActions = () => {
    this.setGridActions(GetTrainingComponentsGridActions(this));
  };

  @action
  loadProjectTeamMembers = async () => {
    const res = await this.projectTeamUserPermissionsProvider.getAllUsersSimple(this.organisationId, this.projectId);
    if (!res || res.isError) return;
    this.setProjectTeamMembers(res.payload);
  };

  @action
  loadProjectTrainingTypes = async () => {
    const res = await this.trainingTypesProvider.getAll(this.organisationId, Category.Component);
    if (!res || res.isError) return;
    this.setProjectTrainingTypes(res.payload);
  };

  @action
  setProjectTeamMembers = projectTeamMembers => {
    this.projectTeamMembers = projectTeamMembers;
    let k = this.gridApi.getColumnDef(CommonColDefFieldNamesEnum.Owner);
    if (!k) return;
    k.cellEditorParams.options = projectTeamMembers;
  };

  @action
  setProjectTrainingTypes = projectTrainingTypes => {
    this.projectTrainingTypes = projectTrainingTypes;
    let k = this.gridApi.getColumnDef(TrainingComponentColDefFieldNamesEnum.TrainingType);
    if (!k) return;
    k.cellEditorParams.options = projectTrainingTypes;
  };

  setGridApi = async e => {
    this.gridApi = e.api;
    if (
      !this.projectTeamMembers &&
      e.type === "gridReady" &&
      this.userCanEditTrainingComponents &&
      this.gridToolbarType !== "link-modal"
    ) {
      await this.loadProjectTeamMembers();
      await this.loadProjectTrainingTypes();
    }
  };

  @action
  setTrainingTypes = trainingTypes => {
    this.trainingTypes = trainingTypes;
  };
  // #endregion

  @action
  setGridActions = (gridActions: any) => {
    this.gridActions = gridActions;
  };

  @action
  onGridStateUpdate = (gridState: AppGridState) => {
    this.gridApi = gridState.gridApi;
    this.selectedItems = gridState.selectedItems || [];
  };

  @action
  setIsColumnStateChanged = (isColumnStateChanged: boolean) => {
    this.isColumnStateChanged = isColumnStateChanged;
    this.generateActions();
  };

  @action
  setIsFilterChanged = (isFilterChanged: boolean) => {
    this.isFilterChanged = isFilterChanged;
    this.generateActions();
  };

  @action
  deleteFieldData = async (itemId: number, field: TrainingComponentField) => {
    const res = await this.trainingComponentsProvider
      .deleteField(this.organisationId, this.projectId, itemId, field)
      .then(async e => {
        await this.onFieldUpdate();
        return e;
      });

    if (res?.isError) return false;
    this.gridToastService.showToast(res.code, res.message);

    return true;
  };

  removeItems = async (itemIds: number[]) => {
    this.httpProgress.showOverlay();
    let res = await this.trainingComponentsProvider.deleteRange(this.organisationId, this.projectId, itemIds);
    if (res && !res.isError) {
      await this.refreshDataHandler();
    }
    this.httpProgress.hideOverlay();
    return res.payload;
  };

  @action
  setSelectedNodes = (gridApi: GridApi) => {
    const nodesToSelect: IRowNode[] = [];
    gridApi.forEachNode(node => {
      if (!!this.preSelectedItemIds?.includes(node.data.id)) {
        nodesToSelect.push(node);
      }
    });
    gridApi.setNodesSelected({ nodes: nodesToSelect, newValue: true });
  };

  @action
  setMicroAddForm = () => {
    this.microTrainingComponentForm = new SingleFormModel();
    this.microTrainingComponentForm.formFields = getEntityNameMicroFormFields(
      this.createMicroTrainingComponent,
      I18n.t("placeholders.myNewName", { entity: I18n.t("entities.trainingComponent") })
    );
  };

  @action
  createMicroTrainingComponent = async () => {
    if (!this.microTrainingComponentForm.isSaving) {
      let microTrainingComponentFormRes = await this.microTrainingComponentForm.submit();
      this.microTrainingComponentForm.isSaving = true;
      if (!microTrainingComponentFormRes) {
        this.microTrainingComponentForm.isSaving = false;
        return;
      }

      microTrainingComponentFormRes = {
        ...microTrainingComponentFormRes,
        projectId: this.projectId
      };

      this.httpProgress.showOverlay();
      let trainingComponentNameExists = await this.trainingComponentsProvider.getByProjectIdAndName(
        this.organisationId,
        this.projectId,
        microTrainingComponentFormRes.name
      );

      if (
        trainingComponentNameExists &&
        !trainingComponentNameExists.isError &&
        trainingComponentNameExists.payload.length
      ) {
        this.httpProgress.hideOverlay();
        let confirmCreateTrainingComponent = await this.confirmCreateTrainingComponent(
          microTrainingComponentFormRes.name
        );
        if (!confirmCreateTrainingComponent) {
          this.microTrainingComponentForm.isSaving = false;
          return;
        }
        this.httpProgress.showOverlay();
      }

      const res = {
        ...microTrainingComponentFormRes
      };
      this.httpProgress.showOverlay();
      const result = await this.trainingComponentsProvider.create(
        this.organisationId,
        this.projectId,
        res as FP.Entities.ITrainingComponent
      );
      await this.refreshDataHandler();

      this.httpProgress.hideOverlay();

      if (!result || result.isError) {
        this.microTrainingComponentForm.isSaving = false;
        return;
      }
      this.microTrainingComponentForm.isSaving = false;

      const trainingComponent = result.payload;
      this.microTrainingComponentForm.resetFields();
      this.toasterService
        .showSuccessToast()
        .setContent(
          <span>{I18n.t("phrases.itemCreatedSuccessfully", { item: I18n.t("entities.trainingComponent") })}</span>
        )
        .startTimer(TOASTER_TOAST_TIME.NORMAL);
      return trainingComponent;
    }
  };

  confirmCreateTrainingComponent = async (name: string): Promise<boolean> => {
    return SHOW_CONFIRM_CREATION_MODAL(name);
  };

  showTrainingComponentConfirmDeleteModal = () => {
    return SHOW_TRAINING_COMPONENT_DELETE_CONFIRM_MODAL(this.selectedItems, this.removeItems);
  };
}
